import _groupBy from 'lodash/groupBy';
import moment from 'moment-timezone';
import PlanningOrder, { Statuses } from 'volta/models/planning-order';
import { TPOField } from 'volta/models/po-group';
import { TCodeDescMode } from 'volta/models/types/collection-view';
import { IPOWorkshopCapacity, TDateField, TPOStatus } from 'volta/models/types/planning-order';
import { TCapacityUnitFrequency } from 'volta/models/types/workshop';
import { indexByFn } from 'volta/utils/array-utils';
import { THueVariationString } from 'volta/utils/style-utils';

type TPOFieldDisplayConfig = Partial<
  Record<
    TPOField,
    {
      color?: THueVariationString;
      label?: (value?: string) => string;
      poValue?: (
        po: PlanningOrder,
        codeDescMode?: TCodeDescMode[],
        isSup?: boolean
      ) => string | undefined;
      poDesc?: (
        po: PlanningOrder,
        codeDescMode?: TCodeDescMode[],
        isSup?: boolean
      ) => string | undefined;
    }
  >
>;

export type TCapaExtended = IPOWorkshopCapacity & {
  __isCapa?: boolean;
  id: string;
  period: string;
  nbPos?: number;
  reorderQtyOptions?: { unit: string; value: number }[];
};

export type IPOGroupItem = TCapaExtended | PlanningOrder;

export interface TDisplayedPOField {
  key: TPOField;
  value?: string;
  desc?: string;
  label?: string;
}

const displayCodeDesc = (
  value: string | undefined,
  codeDescMode: TCodeDescMode[] = ['desc'],
  which: 'code' | 'desc'
) => {
  return codeDescMode.includes(which) ? value : undefined;
};

const displayCode = (value: string | undefined, codeDescMode: TCodeDescMode[] = ['desc']) =>
  displayCodeDesc(value, codeDescMode, 'code');
const displayDesc = (value: string | undefined, codeDescMode: TCodeDescMode[] = ['desc']) =>
  displayCodeDesc(value, codeDescMode, 'desc');

export const POFieldDisplayConfig: TPOFieldDisplayConfig = {
  sku: {
    poValue: (po, codeDescMode) => displayCode(po.skuInfo.part.code, codeDescMode),
    poDesc: (po, codeDescMode, isSup) =>
      displayDesc(
        isSup
          ? po.skuInfo.supplierSkuDescription ?? po.skuInfo.part.description
          : po.skuInfo.part.description,
        codeDescMode
      )
  },
  warehouse: {
    poValue: (po, codeDescMode) => displayCode(po.skuInfo.warehouse.code, codeDescMode),
    poDesc: (po, codeDescMode) => displayDesc(po.skuInfo.warehouse.description, codeDescMode)
  },
  workshop: {
    poValue: (po, codeDescMode) => displayCode(po.workshopInfo?.code, codeDescMode),
    poDesc: (po, codeDescMode) => displayDesc(po.workshopInfo?.description, codeDescMode)
  },
  partType: {
    poValue: (po, codeDescMode) => displayCode(po.skuInfo.partType?.code, codeDescMode),
    poDesc: (po, codeDescMode) => displayDesc(po.skuInfo.partType?.description, codeDescMode)
  },
  deliveryWorkshop: {
    poValue: (po, codeDescMode) => displayCode(po.deliveryWorkshopInfo?.code, codeDescMode),
    poDesc: (po, codeDescMode) => displayDesc(po.deliveryWorkshopInfo?.description, codeDescMode)
  },
  supplier: {
    poValue: (po, codeDescMode) => displayCode(po.supplierInfo?.code, codeDescMode),
    poDesc: (po, codeDescMode) => displayDesc(po.supplierInfo?.description, codeDescMode)
  },
  orderNum: {
    label: (v) => v ?? 'planningOrderStatuses.DRAFT',
    poValue: (po) => po.orderNum ?? po.voltaOrderNum
  },
  acqCode: {
    label: (v) => `acqCodes.${v ?? 'UNKNOWN'}`,
    poValue: (po) => po.acqCode
  }
};

export const PO_SELECTED_CONF = (dateField: TDateField) => ({
  selectedSupplier: (po: PlanningOrder) => po.supplierInfo?.supplierId,
  selectedWorkshop: (po: PlanningOrder) => po.workshopInfo?.workshopId,
  selectedPartType: (po: PlanningOrder) => po.skuInfo.partType?.partTypeId,
  selectedDeliveryWorkshop: (po: PlanningOrder) => po.deliveryWorkshopInfo?.workshopId,
  selectedAcqCode: (po: PlanningOrder) => po.acqCode,
  selectedOrderType: (po: PlanningOrder) => po.orderType,
  selectedDate: (po: PlanningOrder) => po[dateField],
  selectedUnitPrice: (po: PlanningOrder) => po.unitPrice,
  selectedPriority: (po: PlanningOrder) => po.priority ?? 'none',
  selectedForceValidation: (po: PlanningOrder) => po.forceValidation,
  selectedCurrency: (po: PlanningOrder) => po.currency
});

export function formatPeriod(
  frequency: TCapacityUnitFrequency | undefined,
  date: moment.MomentInput
): string {
  switch (frequency) {
    case 'WEEKLY':
      return moment.utc(date).startOf('isoWeek').format('YYYY-MM-DD');
    case 'MONTHLY':
      return moment.utc(date).startOf('month').format('YYYY-MM-DD');
    default:
      return moment.utc(date).format('YYYY-MM-DD');
  }
}

export function getValidPOs(
  pos: PlanningOrder[],
  okStatuses: TPOStatus[] = [],
  checkFn: (po: PlanningOrder) => boolean = () => true
) {
  const selected: PlanningOrder[] = [];

  (pos || []).forEach((plo) => {
    const { status } = plo;

    if ((!okStatuses.length || okStatuses.includes(status)) && checkFn(plo)) {
      selected.push(plo);
    }
  });
  return selected;
}

export function getValidPOIds(
  pos: PlanningOrder[],
  okStatuses: TPOStatus[] = [],
  checkFn: (po: PlanningOrder) => boolean = () => true
) {
  return getValidPOs(pos, okStatuses, checkFn).map((p) => p.id);
}

export function getValidSupplierProposals(pos: PlanningOrder[]) {
  return getValidPOs(pos, [Statuses.LAUNCHED.value, Statuses.COMMITTED.value], (po) =>
    Boolean(po.proposedValues)
  );
}

export function getValidSupplierDraftIds(pos: PlanningOrder[]) {
  return getValidPOIds(
    pos,
    [Statuses.COMMITTED.value],
    (po) => Boolean(po.proposedValues) && po.validationStatus === 'SUPPLIER_DRAFT'
  );
}

export function getValidLaunchableIds(pos: PlanningOrder[], strictMode: boolean = true) {
  return getValidPOIds(
    pos,
    strictMode
      ? [Statuses.NEW.value, Statuses.DRAFT.value]
      : [Statuses.NEW.value, Statuses.DRAFT.value, Statuses.COMMITTED.value, Statuses.SENT.value]
  );
}

export function getValidCommittable(pos: PlanningOrder[], strictMode: boolean = true) {
  return getValidPOs(
    pos,
    strictMode ? [Statuses.LAUNCHED.value] : [Statuses.LAUNCHED.value, Statuses.SENT.value]
  );
}

export function getValidSendable(pos: PlanningOrder[]) {
  return getValidPOs(pos, [Statuses.COMMITTED.value]);
}

export function getValidFinishable(pos: PlanningOrder[]) {
  return getValidPOs(pos, [Statuses.LAUNCHED.value, Statuses.COMMITTED.value, Statuses.SENT.value]);
}

export function buildPosByPeriod(
  pos: PlanningOrder[] = [],
  workshopCapacity: IPOWorkshopCapacity[] = [],
  expandedPeriods: Record<string, boolean> = {},
  dateField: TDateField = 'dueDate'
): {
  periods: IPOGroupItem[];
  capas: TCapaExtended[];
  indexedPosByPeriods?: { [id: string]: PlanningOrder[] };
} {
  const capas: TCapaExtended[] = [];
  const indexedPosByPeriods: {
    [id: string]: Array<PlanningOrder>;
  } = {};

  if (!workshopCapacity || workshopCapacity.length === 0) {
    return { periods: pos, capas };
  }

  const periods: IPOGroupItem[] = [];
  const frequency = workshopCapacity[0].capacityUnitFrequency;
  const groups = _groupBy(pos, (po) => formatPeriod(frequency, po[dateField]));
  const workshopsDict = indexByFn(workshopCapacity, (c) => c.startPeriod);

  Object.entries(groups).forEach(([startPeriod, poItems]) => {
    const startPeriodMoment = moment.utc(startPeriod);
    const capa = workshopsDict[startPeriod] ?? {};
    const capaItem: TCapaExtended = {
      ...capa,
      __isCapa: true,
      id: startPeriodMoment.format(frequency === 'MONTHLY' ? 'MMMYYYY' : 'WoYYYY'),
      period: startPeriodMoment.format(frequency === 'MONTHLY' ? 'MMM YYYY' : 'Wo YYYY'),
      nbPos: poItems.length,
      reorderQtyOptions: buildConvertedQtiesOptions(poItems)
    };

    const isExpanded = expandedPeriods[startPeriod] ?? false;

    if (capaItem.startPeriod) {
      if (poItems.length) {
        periods.push(...[capaItem, ...(isExpanded ? poItems : [])]);
      }
      capas.push(capaItem);
      indexedPosByPeriods[capaItem.startPeriod] = poItems;
    }
  });

  return { periods, capas, indexedPosByPeriods };
}

export function buildConvertedQtiesOptions(pos: PlanningOrder[], constrainedUnit?: string) {
  const optionsMap: Record<string, number> = {};
  pos.forEach((po) => {
    Object.entries(po.convertedQties).forEach(([unit, value]) => {
      if (constrainedUnit && unit !== constrainedUnit) {
        return;
      }
      optionsMap[unit] = (optionsMap[unit] ?? 0) + value;
    });
  });
  return Object.entries(optionsMap).map(([unit, value]) => ({ unit, value }));
}

export function buildSelectionFromGroup(
  selection: IPOGroupItem[],
  indexedPosByPeriods?: { [id: string]: PlanningOrder[] }
): PlanningOrder[] {
  const newSelection: Set<PlanningOrder> = new Set();
  const selectionCount = selection.length;
  const capas = selection.filter((po) => (po as TCapaExtended).__isCapa);

  selection.forEach((po: IPOGroupItem, index: number) => {
    if ((po as TCapaExtended).__isCapa) {
      const capaIndex = capas.indexOf(po as TCapaExtended);
      const isLastCapa = capaIndex === capas.length - 1;
      if (!isLastCapa || selectionCount === 1 || index === selectionCount - 1) {
        const pos = indexedPosByPeriods?.[(po as TCapaExtended).startPeriod];
        if (pos?.length) {
          pos.forEach((p) => newSelection.add(p));
        }
      }
    } else {
      newSelection.add(po as PlanningOrder);
    }
  });

  return [...newSelection];
}
